// This contains the modal with stepperand the <form> tag with react hook form & handle submit function. The different forms displayed are called from ui/Forms for reuse

import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import Modal from '@material-ui/core/Modal'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PurchaseInvoiceDeleteDialog from 'src/components/ui/Dialog/PurchaseInvoiceDeleteDialog'
import { setEditInvoiceModalOpenStatus } from 'src/redux/slices/purchases'
import type { ReduxPurchase } from 'src/types/apiType'
import { Storage } from 'src/utils/amplify/legacy'
import { useAppDispatch, useAppSelector } from 'src/utils/hooks/reduxHooks'
import { delayInMs } from 'src/utils/miscellaneous'

const useStyles = makeStyles((theme) => ({
	quoteSaveModalContent: {
		backgroundColor: theme.palette.background.paper,
		height: '85%',
		width: '85%',
		overflow: 'scroll',
	},
	quoteSaveModal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	formContainer: {
		padding: theme.spacing(2),
		width: '100%',
	},
	buttonsContainer: {
		flexGrow: 1,
		display: 'flex',
		justifyContent: 'end',
	},
	button: {
		margin: theme.spacing(1),
	},
	deleteButton: {
		margin: theme.spacing(1),
		color: 'white',
		backgroundColor: 'red',
		'&:hover': {
			backgroundColor: 'rgb(139,0,0)',
		},
	},
	instructions: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	formFields: {
		display: 'flex',
		flexDirection: 'column',
	},
	formControl: {
		paddingRight: '2rem',
	},
	previewFrame: {
		height: '100%',
		width: '100%',
		display: 'flex',
		position: 'relative',
	},
	editIcon: {
		position: 'absolute',
		right: 0,
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}))

type EditPurchaseInvoiceModalProps = {
	currentPurchaseId: string
	currentPurchaseInfo: ReduxPurchase | null
}

function EditPurchaseInvoiceModal({
	currentPurchaseId,
	currentPurchaseInfo,
}: EditPurchaseInvoiceModalProps) {
	const classes = useStyles()
	const dispatch = useAppDispatch()
	const [loading, setLoading] = useState(false)
	const [fileUrl, setFileUrl] = useState<string | null>(null)
	const editInvoiceModalOpenStatus = useAppSelector(
		(state) => state.purchases.editInvoiceModalOpenStatus
	)
	const deleteInvoiceDialogOpenStatus = useAppSelector(
		(state) => state.purchases.deleteInvoiceDialogOpenStatus
	)

	const { t: tPurchases } = useTranslation('purchases')

	// biome-ignore lint: Document loading - fetches and displays invoice PDF when modal opens
	useEffect(() => {
		setLoading(true)
		const getPDF = async () => {
			let signedUrl: unknown = null
			const invoiceDocuments = currentPurchaseInfo?.invoiceDocuments ?? []
			if (invoiceDocuments.length > 0 && invoiceDocuments[0]?.key) {
				// If key exists only then attempt to get the logo url else set that as null
				signedUrl = await Storage.get(invoiceDocuments[0].key, {
					level: 'protected',
					identityId: invoiceDocuments[0]?.identityId,
				})
			}

			console.log('signedUrl: ', signedUrl)
			setFileUrl(typeof signedUrl === 'string' ? signedUrl : null)
			await delayInMs(500)
			setLoading(false)
		}
		if (currentPurchaseInfo?.invoiceDocuments) {
			getPDF()
		}
	}, [currentPurchaseInfo])

	return !deleteInvoiceDialogOpenStatus ? (
		!loading && editInvoiceModalOpenStatus ? (
			<Modal
				aria-labelledby={'View Invoice'}
				aria-describedby={'View Invoice'}
				open={editInvoiceModalOpenStatus}
				className={classes.quoteSaveModal}
				onClose={() =>
					dispatch(setEditInvoiceModalOpenStatus({ status: false }))
				}
			>
				<div className={classes.quoteSaveModalContent}>
					<Grid container style={{ height: '100%' }}>
						<Paper className={classes.formContainer}>
							{/* <img src={URL.createObjectURL(files)} alt=''></img> */}
							<Grid item xs={12}>
								<Typography variant="h6">
									{tPurchases('viewInvoice')}
								</Typography>
							</Grid>
							<Divider style={{ margin: '0.5rem 0' }} />
							<Grid
								item
								container
								spacing={2}
								style={{ height: '100%' }}
							>
								<Grid
									item
									xs={12}
									style={{
										display: 'flex',
										height: '100%',
									}}
								>
									<div className={classes.previewFrame}>
										<iframe
											src={`${fileUrl}`}
											style={{
												width: '100%',
												height: '100%',
												overflow: 'hidden',
											}}
											scrolling="yes"
											title="preview"
										/>
									</div>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
				</div>
			</Modal>
		) : (
			<Backdrop
				className={classes.backdrop}
				open={editInvoiceModalOpenStatus}
			>
				Loading{' '}
				<CircularProgress
					color="inherit"
					style={{ marginLeft: '0.5rem' }}
				/>
			</Backdrop>
		)
	) : (
		<PurchaseInvoiceDeleteDialog currentPurchaseId={currentPurchaseId} />
	)
}

export default EditPurchaseInvoiceModal
